"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { CommissionDisplay } from "@/app/[locale]/shops/_components/commission-display";
import type { components } from "@donista/shared/api/generated";

type CommissionRangeSummary = components["schemas"]["CommissionRangeSummary"];

interface ScrollTileProps {
  href?: string;
  name: string;
  logoUrl?: string | null;
  shopId?: string;
  commissionRangeSummary?: CommissionRangeSummary;
  subtitle?: React.ReactNode;
  className?: string;
}

export function ScrollTile({ href, name, logoUrl, shopId, commissionRangeSummary, subtitle, className }: ScrollTileProps) {
  const card = (
    <Card className={cn("h-full transition-shadow", href && "cursor-pointer hover:shadow-md", className)}>
      <CardContent className="p-3 sm:p-4 flex flex-col items-center justify-center h-full">
        <div className="relative w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mb-2">
          {logoUrl ? (
            <Image
              src={logoUrl}
              alt={name}
              fill
              sizes="(max-width: 640px) 48px, 64px"
              className="object-contain"
            />
          ) : (
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded bg-muted" />
          )}
        </div>
        <h3 className="font-semibold text-xs text-center line-clamp-1 w-full h-4">
          {name}
        </h3>
        <div className="h-4 mt-1 w-full">
          {subtitle ?? (
            <CommissionDisplay
              commissionRangeSummary={commissionRangeSummary}
              shopId={shopId}
              className="text-xs text-primary text-center truncate w-full"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (!href) return <div className="h-full">{card}</div>;

  return (
    <Link href={href} className="block h-full">
      {card}
    </Link>
  );
}
